import { firstValueFrom, Observable, Subscriber, Subscription } from 'rxjs';
import { EventEmitter, Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { ChangeTyped, isDefined, Query, QueryService, SearchResult } from '@damofire/data-commons';
import { DocumentChangeType, DocumentData } from 'firebase/firestore';
import { Query as FirestoreQuery } from '@firebase/firestore';

export enum ServiceEventType {
  CREATED,
  UPDATED,
  DELETED,
}

export class ServiceEvent<T> {
  constructor(
    private readonly serviceEventType: ServiceEventType,
    private readonly eventItems: Change<T>[],
  ) {}
  get type(): ServiceEventType {
    return this.serviceEventType;
  }

  get items(): Change<T>[] {
    return cloneDeep(this.eventItems);
  }
}

interface Change<T> {
  before: T;
  after: T;
}

export abstract class ObservableService<T> {
  private readonly pevents: EventEmitter<ServiceEvent<T>> = new EventEmitter<ServiceEvent<T>>();
  constructor(eventEmitter?: EventEmitter<ServiceEvent<T>>) {
    if (eventEmitter) {
      this.pevents = eventEmitter;
    }
  }

  public events(): Observable<ServiceEvent<T>> {
    return this.pevents.asObservable();
  }

  protected publishEvent(type: ServiceEventType, ...items: (Change<T> | T)[]): void {
    const changes: Change<T>[] = items.map((item) => {
      if (type === ServiceEventType.CREATED) {
        return { before: null, after: item } as Change<T>;
      } else if (type === ServiceEventType.DELETED) {
        return { before: item, after: null } as Change<T>;
      } else if ('before' in (item as Change<T>) && 'after' in (item as Change<T>)) {
        return item as Change<T>;
      } else {
        throw new Error('Before and After have to be provided for an update event');
      }
    });
    this.pevents.next(new ServiceEvent<T>(type, changes));
  }
}

@Injectable()
export abstract class ObservableQueryService extends QueryService {
  abstract observeChanges<T>(collectionRef: FirestoreQuery<T | DocumentData>, events?: DocumentChangeType[]): Observable<ChangeTyped<T[]>>;
  abstract collection<T>(path: string, q: Query, searchFields?: string[]): Promise<SearchResult<T>>;
}

export const toPromise = <T>(observable: Observable<T>, condition: (next: T) => boolean = isDefined, initialValue?: T, filter?: (next: T) => T): Promise<T> => {
  return firstValueFrom(toObservable(observable, condition, initialValue, filter));
};

const handleInitialValue = <T>(observer: Subscriber<T>, initialValue?: T | Promise<T>, filter?: (next: T) => T, log?: string): void => {
  if (initialValue) {
    if (initialValue instanceof Promise) {
      initialValue.then((resolved: T) => {
        console.log(`Initial value given, propagating value to observer. [${log}]`);
        const filtered = filter ? filter(resolved) : resolved;
        observer.next(filtered);
      });
    } else {
      console.log(`Initial value given, propagating value to observer. [${log}]`);
      const filtered = filter ? filter(initialValue) : initialValue;
      observer.next(filtered);
    }
  }
};

const subscribeAndUpdate = <T>(observable: Observable<T>, observer: Subscriber<T>, condition: (next: T) => boolean, filter?: (next: T) => T, log?: string): Subscription => {
  return observable.subscribe((next: T) => {
    console.log(`New value given, evaluating condition before propagation. [${log}]`);
    if (condition(next)) {
      console.log(`Value meets condition, propagating.`);
      const filtered = filter ? filter(next) : next;

      if (filter) {
        console.log(`Value was filtered according to callback. [${log}]`, Array.isArray(filtered) ? filtered.length : undefined);
      }

      observer.next(filtered);
    }
  });
};

export const toObservable = <T>(
  observable: Observable<T>,
  condition: (next: T) => boolean,
  initialValue?: T | Promise<T>,
  filter?: (next: T) => T,
  log?: string,
  complete?: () => void,
): Observable<T> => {
  return new Observable((observer: Subscriber<T>) => {
    handleInitialValue(observer, initialValue, filter, log);
    const iSub = subscribeAndUpdate(observable, observer, condition, filter, log);

    return function unsubscribe() {
      console.log(`unsubscribe called on observable. [${log}]`);
      iSub.unsubscribe();

      if (complete) {
        complete();
      }
    };
  });
};
